<script setup lang="ts">
import { useVModel } from '@vueuse/core'

import { Form, RealState } from '~/layers/forms/models'
import { requiredValidator } from '~/utils/validators'

interface Props {
  modelValue: RealState
}

const props = defineProps<Props>()
const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)

const v$ = useValidation(
  () => ({
    propertyName: { required: requiredValidator },
    propertyDescription: { required: requiredValidator },
    valuablesDescription: { required: requiredValidator },
    roofType: { required: requiredValidator },
    squareMetersArea: { required: requiredValidator },
    use: { required: requiredValidator },
    buildingType: { required: requiredValidator },
    waterSurfaceDistance: { required: requiredValidator },
    condition: { required: requiredValidator },
  }),
  model,
  { $scope: Form.realState, $registerAs: Form.realState }
)
</script>

<template>
  <FormsGlobalContainer
    v-slot="{ sectionClasses }"
    :identifier="Form.realState"
  >
    <FormsGlobalTitle
      text="Información del inmueble"
      icon-class="icon-[ph--house-line-bold]"
    />

    <InputField
      label="Nombre del inmueble"
      class="md:col-span-2"
      :error="v$.propertyName?.$error"
      :error-message="v$.propertyName?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.propertyName" />
    </InputField>

    <InputField
      label="Descripción del inmueble"
      class="md:col-span-4"
      :error="v$.propertyDescription?.$error"
      :error-message="v$.propertyDescription?.$errors[0]?.$message.toString()"
    >
      <PvTextarea v-model="model.propertyDescription" />
    </InputField>

    <FormsGlobalCurrencyInput
      v-model="model.propertyValue"
      :validation-scope="Form.realState"
      inputs-class="md:col-span-2"
    />

    <InputField
      label="Descripción de mobiliarios y objetos valiosos"
      class="md:col-span-4"
      :error="v$.valuablesDescription?.$error"
      :error-message="v$.valuablesDescription?.$errors[0]?.$message.toString()"
    >
      <PvTextarea v-model="model.valuablesDescription" />
    </InputField>

    <FormsGlobalCurrencyInput
      v-model="model.valuablesValue"
      label="Valor de mobiliarios y objetos valiosos"
      :validation-scope="Form.realState"
      register-validation-as="valuables-currency"
      inputs-class="md:col-span-2"
    />

    <InputField
      label="Tipo de techo"
      class="md:col-span-2"
      :error="v$.roofType?.$error"
      :error-message="v$.roofType?.$errors[0]?.$message.toString()"
    >
      <PvInputText v-model="model.roofType" />
    </InputField>

    <InputField
      label="Área (metros cuadrados)"
      class="md:col-span-2"
      :error="v$.squareMetersArea?.$error"
      :error-message="v$.squareMetersArea?.$errors[0]?.$message.toString()"
    >
      <PvInputNumber v-model="model.squareMetersArea" />
    </InputField>

    <InputField
      label="Uso del inmueble"
      class="md:col-span-2"
      :error="v$.use?.$error"
      :error-message="v$.use?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.use"
        option-label="label"
        option-value="code"
        :options="RealState.getUseTypeList()"
      />
    </InputField>

    <InputField
      label="Tipo de construcción"
      class="md:col-span-2"
      :error="v$.buildingType?.$error"
      :error-message="v$.buildingType?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.buildingType"
        option-label="label"
        option-value="code"
        :options="RealState.getBuildingTypeList()"
      />
    </InputField>

    <InputField
      label="Distancia al mar o lago"
      class="md:col-span-2"
      :error="v$.waterSurfaceDistance?.$error"
      :error-message="v$.waterSurfaceDistance?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.waterSurfaceDistance"
        option-label="label"
        option-value="code"
        :options="RealState.distanceToWaterSurfaceTypeList()"
      />
    </InputField>

    <InputField
      label="Condición del inmueble"
      class="md:col-span-2"
      :error="v$.condition?.$error"
      :error-message="v$.condition?.$errors[0]?.$message.toString()"
    >
      <PvSelect
        v-model="model.condition"
        option-label="label"
        option-value="code"
        :options="RealState.getConditionTypeList()"
      />
    </InputField>

    <div
      class="full col-span-full items-end"
      :class="sectionClasses({ cols: 4 })"
    >
      <InputContainer
        label="Mangueras"
        :error="v$.hasHoses?.$error"
        :error-message="v$.hasHoses?.$errors[0]?.$message.toString()"
      >
        <PvCheckbox
          v-model="model.hasHoses"
          :binary="true"
        />
      </InputContainer>

      <InputContainer
        label="Sistema de Alarma de Incendio"
        :error="v$.hasFireAlarm?.$error"
        :error-message="v$.hasFireAlarm?.$errors[0]?.$message.toString()"
      >
        <PvCheckbox
          v-model="model.hasFireAlarm"
          :binary="true"
        />
      </InputContainer>

      <InputContainer
        label="Sistema de Alarma de Robo"
        :error="v$.hasTheftAlarm?.$error"
        :error-message="v$.hasTheftAlarm?.$errors[0]?.$message.toString()"
      >
        <PvCheckbox
          v-model="model.hasTheftAlarm"
          :binary="true"
        />
      </InputContainer>

      <InputContainer
        label="Rejas de Hierro"
        :error="v$.hasSteelFence?.$error"
        :error-message="v$.hasSteelFence?.$errors[0]?.$message.toString()"
      >
        <PvCheckbox
          v-model="model.hasSteelFence"
          :binary="true"
        />
      </InputContainer>
    </div>
  </FormsGlobalContainer>
</template>
